import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex min-h-0 flex-1 flex-col bg-gray-900"
}
const _hoisted_2 = { class: "py-2 bg-black" }
const _hoisted_3 = { class: "flex flex-shrink-0 items-center px-4" }
const _hoisted_4 = {
  key: 0,
  class: "text-gray-200 text-xs ml-3.5"
}
const _hoisted_5 = { class: "mt-1 inline-flex gap-1 items-center" }
const _hoisted_6 = { class: "flex items-center" }
const _hoisted_7 = { class: "text-gray-800 bg-gray-300 rounded px-1" }
const _hoisted_8 = { class: "flex flex-1 flex-col overflow-y-auto pb-4" }
const _hoisted_9 = {
  class: "flex-1",
  "aria-label": "Sidebar"
}
const _hoisted_10 = { class: "px-2" }
const _hoisted_11 = {
  key: 0,
  class: "my-6 border-gray-700"
}
const _hoisted_12 = {
  key: 2,
  class: "px-2 pb-2 bg-gray-900"
}
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 3,
  "data-tour": "setup",
  class: "px-2 pb-2 bg-gray-900"
}
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "flex flex-1 flex-col overflow-y-auto pb-4" }
const _hoisted_17 = {
  class: "flex-1",
  "aria-label": "Sidebar"
}
const _hoisted_18 = { class: "px-2" }
const _hoisted_19 = {
  key: 0,
  class: "my-6 border-gray-700"
}
const _hoisted_20 = {
  key: 0,
  class: "px-2 pb-2 bg-gray-900"
}
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 1,
  class: "px-2 pb-2 bg-gray-900"
}

import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid'
import { ArrowDownTrayIcon, BellIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import AppNavItem from '@/components/AppNavItem.vue'
import PlatformSwitcher from '@/components/PlatformSwitcher.vue'
import SetupBadge from '@/components/setup/SetupBadge.vue'
import SidebarAccountCard from '@/components/SidebarAccountCard.vue'
import { useSetupState } from '@/composables/setupState'
import { useEntitlements } from '@/composables/useEntitlements'
import {
  hasBeenPushPrompted,
  isMobileDevice,
  markPushPrompted,
  usePushNotifications
} from '@/composables/usePushNotifications'
import { usePwaInstall } from '@/composables/usePwaInstall'
import { useSettingsAccess } from '@/composables/useSettingsAccess'
import { sectionNav } from '@/config/nav'
import { isNavLinkActive, isSectionPath } from '@/config/nav/helpers'
import { cateringSections, ecommerceSections, franchiseSections } from '@/config/navSections'
import { buildSettingsTabs, settingsTabForPath } from '@/config/settingsTabs'
import { useRevealed } from '@/helpers/revelio'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapType, mapTypeIncludingInactive } from '@/store/mappers'

const FRANCHISE_ACCOUNT_PATH = '/franchise/account'

// A franchise principal has no storefront or Bottle number of their own, so the card names the
// franchise, shows who is signed in and opens the franchise account page instead of Settings.

export default {
  __name: 'AppSidebar',
  props: {
  variant: { type: String, default: 'desktop' }
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {


const emit = __emit

const store = useStore()
const route = useRoute()
const { visible: setupVisible } = useSetupState()
const { canInstall, promptInstall } = usePwaInstall()
const {
  isSupported: pushSupported,
  isLoading: pushLoading,
  permissionState,
  subscribe: pushSubscribe
} = usePushNotifications()

// Safari (macOS + iOS) requires a user gesture to call Notification.requestPermission(),
// so the auto-prompt watcher silently no-ops there. We show a manual button instead.
const isSafari =
  typeof navigator !== 'undefined' && /^((?!chrome|crios|android|fxios|edgios).)*safari/i.test(navigator.userAgent)

const pushPromptEligible = computed(() => {
  if (!isMobileDevice) return false
  const userId = store.state.loggedInUser?.id
  if (!userId) return false
  const notifications = Object.values(store.state.objects?.notification ?? {})
  const hasPushEnabled = notifications.some(n => n.attributes?.web_push_enabled)
  if (!hasPushEnabled) return false
  if (!pushSupported.value) return false
  if (permissionState.value === 'granted' || permissionState.value === 'denied') return false
  if (hasBeenPushPrompted(userId)) return false
  return true
})

const showEnablePushButton = computed(() => pushPromptEligible.value && isSafari)

watch(pushPromptEligible, async shouldPrompt => {
  if (!shouldPrompt) return
  if (isSafari) return
  const userId = store.state.loggedInUser?.id
  if (!userId) return
  try {
    await pushSubscribe()
  } finally {
    markPushPrompted(userId)
  }
})

const enablePush = async () => {
  const userId = store.state.loggedInUser?.id
  if (!userId) return
  try {
    await pushSubscribe()
  } finally {
    markPushPrompted(userId)
  }
}

watch(
  () => store.state.loggedInUser?.id,
  userId => {
    if (!userId) return
    api.call('GET', 'notifications').catch(() => {})
  },
  { immediate: true }
)

onMounted(() => {
  // A principal has no inbox or team of their own, so there are no conversations or roles to load.
  if (store.getters.isFranchise) return
  api.call('GET', 'conversation_user_pairs')
  if (privilegeService.canAccessRoute('/users/roles')) {
    api.call('GET', 'system_user_roles?included=system_users,system_user_privileges')
  }
})

const hideNumbers = computed(() => store.state.hideNumbers)

const isCatering = computed(() => store.getters.isCatering)
const hasMultiplePlatforms = computed(() => store.getters.hasMultiplePlatforms)
const isFranchise = computed(() => store.getters.isFranchise)
const showSearch = computed(() => !isCatering.value && !isFranchise.value)

const sectionsFor = ctx => {
  if (isFranchise.value) return franchiseSections()
  return isCatering.value ? cateringSections(ctx) : ecommerceSections(ctx)
}

const entitlements = useEntitlements()

const sections = computed(() => {
  const ctx = {
    subdomain: subdomain.value,
    featureState: entitlements.state,
    hideNumbers: hideNumbers.value,
    capabilities: user.value?.attributes?.capabilities ?? []
  }
  return sectionsFor(ctx).filter(section => {
    if (section.type === 'separator') return true
    if (section.type === 'ai') return ctx.capabilities.includes('ai')
    if (section.route) return privilegeService.canAccessRoute(section.route)
    return true
  })
})

const isHighlighted = x => {
  const bases = sectionNav(x.name)?.bases
  return bases ? isSectionPath(route.path, bases) : route.path.startsWith(x.route)
}

// use client hints if available, fall back to deprecated navigator.platform if not
const isMac = computed(() =>
  navigator.userAgentData ? navigator.userAgentData.platform === 'macOS' : navigator.platform === 'MacIntel'
)

const user = computed(() => store.state.loggedInUser)
const name = computed(() => user.value.attributes?.user_name || user.value.attributes?.handle)
const subdomain = computed(() => mapRelationship(user.value.relationships?.default_subdomain.data)?.attributes.name)
const avatar = computed(() => user.value.attributes?.avatar_url)

// A merchant with texting killed has no usable Bottle number, so the warning at
// the top replaces the number in the card rather than sitting alongside it.
// A principal does not text from the franchisee's number, so its warning is not theirs to act on.
const textingDisabled = computed(
  () => !isFranchise.value && user.value?.attributes?.capabilities?.includes('kill-twilio')
)
const phone = computed(() => (textingDisabled.value ? '' : user.value?.attributes?.phone_formatted))

const revealed = useRevealed()
const expandedLists = ref([])

const showAll = name => {
  expandedLists.value = [...expandedLists.value, name]
}

const showFewer = name => {
  expandedLists.value = expandedLists.value.filter(x => x !== name)
}

const navContext = computed(() => ({
  isCatering: isCatering.value,
  capabilities: user.value?.attributes?.capabilities ?? [],
  hideNumbers: hideNumbers.value,
  featureState: entitlements.state,
  expanded: expandedLists.value,
  revealed: revealed.value,
  path: route.path,
  mapType,
  mapTypeIncludingInactive
}))

const childrenBySection = computed(() =>
  Object.fromEntries(
    sections.value.map(section => [
      section.name,
      // A locked section is one link to its locked page, with nothing to expand.
      section.locked ? [] : sectionNav(section.name)?.build(navContext.value) ?? []
    ])
  )
)

const navChildren = section => childrenBySection.value[section.name] ?? []

const settingsAccess = useSettingsAccess()

const settingsTabs = computed(() =>
  buildSettingsTabs({
    hasFullAccess: settingsAccess.hasFullAccess.value,
    capabilities: settingsAccess.capabilities.value,
    canManageUsers: settingsAccess.canManageUsers.value,
    isCatering: settingsAccess.isCatering.value
  })
)

const onSettingsPage = computed(() => Boolean(settingsTabForPath(settingsTabs.value, route.path)))

const accountCard = computed(() => {
  if (isFranchise.value) {
    const franchise = store.state.franchise?.attributes ?? {}
    return {
      name: franchise.name ?? '',
      subtitle: franchise.principal_email ?? '',
      to: FRANCHISE_ACCOUNT_PATH,
      highlighted: route.path.startsWith(FRANCHISE_ACCOUNT_PATH)
    }
  }
  return {
    name: name.value,
    avatar: avatar.value,
    subdomain: subdomain.value,
    phone: phone.value,
    highlighted: onSettingsPage.value
  }
})

const openSection = ref(null)

const toggleSection = name => {
  openSection.value = openSection.value === name ? null : name
}

watch(
  () => route.path,
  () => {
    const current = sections.value.find(section => navChildren(section).length && isHighlighted(section))
    openSection.value = current?.name ?? null
  },
  { immediate: true }
)

const requestedLists = new Set()
// `fetches` is a static list, or a function of the nav context when it depends on the plan.
const resolveFetches = nav => {
  const fetches = nav?.fetches ?? []
  return typeof fetches === 'function' ? fetches(navContext.value) : fetches
}
watch(
  openSection,
  name => {
    resolveFetches(sectionNav(name))
      .filter(path => !requestedLists.has(path))
      .forEach(path => {
        requestedLists.add(path)
        api.call('GET', path)
      })
  },
  { immediate: true }
)

const isRowHighlighted = section =>
  isHighlighted(section) && !(navChildren(section).length && openSection.value === section.name)

const hasAppAndPushNotifications = computed(() =>
  user.value?.attributes?.capabilities?.includes('app-and-push-notifications')
)

const unreadCount = computed(() => mapType('conversation_user_pair')?.filter(x => x.attributes['has_unread?']).length)

watch(unreadCount, count => {
  if (navigator.setAppBadge) {
    count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge()
  }
})

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (__props.variant === 'desktop')
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_router_link, { to: "/orders" }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createElementVNode("svg", {
                  class: "w-20 h-8 text-gray-200 fill-current",
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "167.01",
                  height: "35",
                  viewBox: "0 0 167.01 35"
                }, [
                  _createElementVNode("path", {
                    d: "M57.24,16.55C59,23,55.3,31.88,45.41,34.43c-7.3,1.88-11.8-1-13.29-6.61C30.41,21.43,33.93,12.57,44.06,10c7.11-1.83,11.66.9,13.18,6.59ZM34.73,27.05c1.28,4.81,5,6.43,10.07,5.12,7.63-2,11.38-9.41,9.92-14.87-1.28-4.81-5.24-6.26-10.19-5C36.76,14.32,33.23,21.45,34.73,27.05Z",
                    transform: "translate(0)"
                  }),
                  _createElementVNode("path", {
                    d: "M57.24,16.55C59,23,55.3,31.88,45.41,34.43c-7.3,1.88-11.8-1-13.29-6.61C30.41,21.43,33.93,12.57,44.06,10c7.11-1.83,11.66.9,13.18,6.59ZM34.73,27.05c1.28,4.81,5,6.43,10.07,5.12,7.63-2,11.38-9.41,9.92-14.87-1.28-4.81-5.24-6.26-10.19-5C36.76,14.32,33.23,21.45,34.73,27.05Z",
                    transform: "translate(0)"
                  }),
                  _createElementVNode("path", {
                    d: "M112.48,13.26a19.73,19.73,0,0,1,.78-2.62c-1.8.14-7.94.18-13.83.18.93-1.86,2.8-5.37,3.91-7.56a19.91,19.91,0,0,1-3.26.33c-1,2-2.74,5.42-3.62,7.23H74.2C75.15,9,77,5.45,78.14,3.25a19.91,19.91,0,0,1-3.26.33c-1,2-2.75,5.42-3.63,7.24-4.48,0-8.42-.1-9.64-.19A15.17,15.17,0,0,1,61,13.3c1.22-.09,4.87-.14,9.15-.14-2.91,6-5.06,10.9-5.06,15.06,0,4,2.09,5.93,6,6,4.38,0,8.66-2.25,13.48-7.79a8.79,8.79,0,0,1-2.33-1.92c-4.09,5.41-7.45,7.22-10.81,7.22-2.49,0-3.75-1.24-3.75-3.92,0-3.44,2-8,5.35-14.63H95.25c-2.91,6-5.06,10.9-5.06,15.06,0,4,2.09,5.93,6,6,4.38,0,8.66-2.25,13.48-7.8a9,9,0,0,1-2.33-1.91c-4.09,5.4-7.46,7.22-10.81,7.22-2.49,0-3.75-1.25-3.75-3.92,0-3.44,2-8,5.35-14.63C104.3,13.11,110.68,13.17,112.48,13.26Z",
                    transform: "translate(0)"
                  }),
                  _createElementVNode("path", {
                    d: "M165.14,26.26a15.38,15.38,0,0,0,1.87,2,22,22,0,0,1-14.37,6c-8.31.56-12.52-3-12.92-8.64-.52-7.45,5-15.25,15.94-16,6.35-.44,9.76,2.35,10,5.87.4,5.71-7,9.08-23.25,9.84.49,4.87,4.2,7,10.7,6.51A17.23,17.23,0,0,0,165.14,26.26ZM142.46,23c14.09-.76,20.54-3.23,20.26-7.2-.19-2.66-2.8-4-7.38-3.67C147.86,12.62,143.11,17.46,142.46,23Z",
                    transform: "translate(0)"
                  }),
                  _createElementVNode("path", {
                    d: "M134.12,24.5c-4.09,5.4-7.45,7.22-10.8,7.22a4.18,4.18,0,0,1-2.29-.6,2.92,2.92,0,0,1-1.32-2.64c0-5.6,8.71-17.17,8.71-23a4.07,4.07,0,0,0-2.44-4A5.8,5.8,0,0,0,123.52,1a17.6,17.6,0,0,0-6.43,1.48,14.08,14.08,0,0,1,1,2.49,12.38,12.38,0,0,1,5.12-1.39c1.84,0,2.41,1.09,2.41,1.91,0,4.64-8.76,16.69-8.76,23.19a4.86,4.86,0,0,0,2.47,4.6l.12.07a7.3,7.3,0,0,0,3.51.93c4.38,0,8.67-2.25,13.49-7.8a8.8,8.8,0,0,1-2.34-1.91",
                    transform: "translate(0)"
                  }),
                  _createElementVNode("path", {
                    d: "M20.84,9.61c-4.68,0-8.7,2.1-11.91,7.8H8.52c2.2-5.14,4.09-10.13,4.09-13.11S11,0,8.12,0A17.8,17.8,0,0,0,1.7,1.48,14.45,14.45,0,0,1,2.72,4,12.18,12.18,0,0,1,7.83,2.58c1.66,0,2.34.72,2.34,2.11,0,3.49-4.47,14-10.17,28.66A12.49,12.49,0,0,1,2.63,34c.83-2,1.57-3.87,2.26-5.56,1.67,3.67,4.06,6.34,8.57,6.34,8.19,0,14.91-9,14.91-17.19,0-4.85-2.6-7.95-7.53-7.95ZM13.51,32.2c-3.21,0-5.39-1.8-7.68-6,4.42-10.85,10.53-14.1,14.7-14.1,3.16,0,4.93,1.75,4.93,5.65,0,6.39-5.34,14.49-12,14.49",
                    transform: "translate(0)"
                  })
                ], -1)
              ]))]),
              _: 1
            })
          ]),
          (textingDisabled.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(ExclamationTriangleIcon), { class: "h-4 w-4 text-yellow-300" }),
                  _cache[7] || (_cache[7] = _createTextVNode(" Texting disabled, ", -1)),
                  _cache[8] || (_cache[8] = _createElementVNode("a", {
                    href: "mailto:team@bottle.com",
                    class: "underline"
                  }, "contact support", -1))
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        (hasMultiplePlatforms.value)
          ? (_openBlock(), _createBlock(PlatformSwitcher, { key: 0 }))
          : _createCommentVNode("", true),
        (showSearch.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              "data-tour": "search",
              class: "my-2 mx-2 p-2 bg-gray-200 rounded select-none flex justify-between cursor-pointer",
              onClick: _cache[0] || (_cache[0] = $event => (_unref(store).commit('TOGGLE_OMNI')))
            }, [
              _createElementVNode("span", _hoisted_6, [
                _createVNode(_unref(MagnifyingGlassIcon), { class: "w-4 h-4 text-gray-800 mr-1.5" }),
                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-sm text-gray-800" }, "Search everything...", -1))
              ]),
              _createElementVNode("span", _hoisted_7, _toDisplayString(isMac.value ? '⌘K' : 'Ctrl K'), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("nav", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: section.name || index,
                  class: "my-3 space-y-1"
                }, [
                  (section.type === 'separator' && index > 0)
                    ? (_openBlock(), _createElementBlock("hr", _hoisted_11))
                    : _createCommentVNode("", true),
                  (section.type !== 'separator')
                    ? (_openBlock(), _createBlock(AppNavItem, {
                        key: 1,
                        section: section,
                        highlighted: isRowHighlighted(section),
                        "unread-count": unreadCount.value,
                        children: navChildren(section),
                        open: openSection.value === section.name,
                        "is-child-active": child => _unref(isNavLinkActive)(_unref(route).path, child),
                        onToggle: $event => (toggleSection(section.name)),
                        onShowMore: showAll,
                        onShowFewer: showFewer
                      }, null, 8, ["section", "highlighted", "unread-count", "children", "open", "is-child-active", "onToggle"]))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ])
          ])
        ]),
        (showEnablePushButton.value && !isFranchise.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("button", {
                class: "flex items-center gap-2 w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-md transition-colors",
                disabled: _unref(pushLoading),
                onClick: enablePush
              }, [
                _createVNode(_unref(BellIcon), { class: "h-4 w-4 shrink-0" }),
                _createTextVNode(" " + _toDisplayString(_unref(pushLoading) ? 'Enabling...' : 'Enable push notifications'), 1)
              ], 8, _hoisted_13)
            ]))
          : _createCommentVNode("", true),
        (_unref(setupVisible) && !isFranchise.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(SetupBadge)
            ]))
          : _createCommentVNode("", true),
        _createVNode(SidebarAccountCard, _mergeProps({ "data-tour": "account" }, accountCard.value), null, 16)
      ]))
    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
        _createElementVNode("div", {
          class: "h-0 flex-1 overflow-y-auto pb-4 bg-gray-900",
          onClick: _cache[2] || (_cache[2] = $event => (emit('close')))
        }, [
          (hasMultiplePlatforms.value)
            ? (_openBlock(), _createBlock(PlatformSwitcher, { key: 0 }))
            : _createCommentVNode("", true),
          (showSearch.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "m-4 p-2 w-72 bg-gray-200 rounded select-none flex justify-between cursor-pointer",
                onClick: _cache[1] || (_cache[1] = $event => (_unref(store).commit('TOGGLE_OMNI')))
              }, [
                _createElementVNode("span", _hoisted_15, [
                  _createVNode(_unref(MagnifyingGlassIcon), { class: "w-4 h-4 text-gray-800 ml-1 mr-2" }),
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "text-sm text-gray-800" }, "Search everything...", -1))
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("nav", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: section.name || index,
                    class: "my-3 space-y-1"
                  }, [
                    (section.type === 'separator' && index > 0)
                      ? (_openBlock(), _createElementBlock("hr", _hoisted_19))
                      : _createCommentVNode("", true),
                    (section.type !== 'separator')
                      ? (_openBlock(), _createBlock(AppNavItem, {
                          key: 1,
                          section: section,
                          highlighted: isRowHighlighted(section),
                          "unread-count": unreadCount.value,
                          children: navChildren(section),
                          open: openSection.value === section.name,
                          "is-child-active": child => _unref(isNavLinkActive)(_unref(route).path, child),
                          onToggle: $event => (toggleSection(section.name)),
                          onShowMore: showAll,
                          onShowFewer: showFewer
                        }, null, 8, ["section", "highlighted", "unread-count", "children", "open", "is-child-active", "onToggle"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ])
          ])
        ]),
        (showEnablePushButton.value && !isFranchise.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createElementVNode("button", {
                class: "flex items-center gap-2 w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-md transition-colors",
                disabled: _unref(pushLoading),
                onClick: enablePush
              }, [
                _createVNode(_unref(BellIcon), { class: "h-4 w-4 shrink-0" }),
                _createTextVNode(" " + _toDisplayString(_unref(pushLoading) ? 'Enabling...' : 'Enable push notifications'), 1)
              ], 8, _hoisted_21)
            ]))
          : _createCommentVNode("", true),
        (hasAppAndPushNotifications.value && _unref(canInstall))
          ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
              _createElementVNode("button", {
                class: "flex items-center gap-2 w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-md transition-colors",
                onClick: _cache[3] || (_cache[3] = (...args) => (_unref(promptInstall) && _unref(promptInstall)(...args)))
              }, [
                _createVNode(_unref(ArrowDownTrayIcon), { class: "h-4 w-4 shrink-0" }),
                _cache[11] || (_cache[11] = _createTextVNode(" Install Bottle app ", -1))
              ])
            ]))
          : _createCommentVNode("", true),
        (_unref(setupVisible) && !isFranchise.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              class: "px-2 pb-2 bg-gray-900",
              onClick: _cache[4] || (_cache[4] = $event => (emit('close')))
            }, [
              _createVNode(SetupBadge)
            ]))
          : _createCommentVNode("", true),
        _createVNode(SidebarAccountCard, _mergeProps(accountCard.value, {
          onNavigate: _cache[5] || (_cache[5] = $event => (emit('close')))
        }), null, 16)
      ], 64))
}
}

}